import React, { useState, useEffect } from 'react';
import { X, ChevronLeft, ChevronRight, MapPin, Calendar, FileText, Users, CheckCircle } from 'lucide-react';

const OnboardingTour = ({ onComplete, onSkip }) => {
  const [currentStep, setCurrentStep] = useState(0);
  
  const steps = [
    {
      icon: CheckCircle,
      title: "You're in the Right Place",
      subtitle: "Welcome to AbleFam",
      description: "We built this platform because we understand your journey. Whether you're searching for accessible venues, planning activities, or navigating IEP meetings - you're not alone. Our community of parents is here to help.",
      color: "blue",
      tip: "This app was created by parents, for parents of children with disabilities."
    },
    {
      icon: MapPin,
      title: "Accessibility Map",
      subtitle: "Find Family-Friendly Places",
      description: "Search for wheelchair-accessible venues, sensory-friendly locations, and places reviewed by families like yours. Toggle between map and list views based on your preference.",
      color: "green",
      tip: "Look for 'Verified' badges for officially confirmed accessibility, or 'Community Reported' for parent-submitted info."
    },
    {
      icon: Calendar,
      title: "Inclusive Events",
      subtitle: "Discover Sensory-Friendly Activities",
      description: "Find adaptive sports, sensory-friendly movie screenings, support groups, and inclusive events happening near you. Filter by event type and accessibility accommodations.",
      color: "purple",
      tip: "Events are aggregated from trusted Minnesota organizations like PACER Center, AuSM, and local museums."
    },
    {
      icon: FileText,
      title: "IEP Resource Hub",
      subtitle: "AI-Powered IEP Support",
      description: "Create measurable IEP goals with our AI assistant, improve existing goals, and access state-specific resources. No more struggling alone with IEP paperwork.",
      color: "orange",
      tip: "The Goal Creator helps you write SMART goals that are specific, measurable, and achievable."
    },
    {
      icon: Users,
      title: "Parent Community",
      subtitle: "Connect with Other Families",
      description: "Join discussions on topics that matter - from potty training tips to navigating school systems. Share experiences, ask questions, and find support from parents who truly understand.",
      color: "pink",
      tip: "Every topic includes curated resources and helpful blog links from trusted sources."
    }
  ];

  const currentStepData = steps[currentStep];
  const Icon = currentStepData.icon;
  const isLastStep = currentStep === steps.length - 1;
  const isFirstStep = currentStep === 0;

  const colorClasses = {
    blue: { bg: 'bg-blue-100', text: 'text-blue-600', button: 'bg-blue-600 hover:bg-blue-700' },
    green: { bg: 'bg-green-100', text: 'text-green-600', button: 'bg-green-600 hover:bg-green-700' },
    purple: { bg: 'bg-purple-100', text: 'text-purple-600', button: 'bg-purple-600 hover:bg-purple-700' },
    orange: { bg: 'bg-orange-100', text: 'text-orange-600', button: 'bg-orange-600 hover:bg-orange-700' },
    pink: { bg: 'bg-pink-100', text: 'text-pink-600', button: 'bg-pink-600 hover:bg-pink-700' }
  };

  const colors = colorClasses[currentStepData.color];

  const handleNext = () => {
    if (isLastStep) {
      onComplete();
    } else {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(prev => prev - 1);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className={`${colors.bg} px-6 py-8 text-center relative`}>
          <button
            onClick={onSkip}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-700 p-1"
            aria-label="Skip tour"
          >
            <X size={20} />
          </button>
          
          <div className={`w-20 h-20 ${colors.bg} border-4 border-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg`}>
            <Icon size={40} className={colors.text} />
          </div>
          
          <p className={`text-sm font-medium ${colors.text} mb-1`}>{currentStepData.subtitle}</p>
          <h2 className="text-2xl font-bold text-gray-900">{currentStepData.title}</h2>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          <p className="text-gray-700 text-center mb-4 leading-relaxed">
            {currentStepData.description}
          </p>
          
          {/* Tip Box */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-6">
            <p className="text-sm text-yellow-800">
              <span className="font-semibold">💡 Tip:</span> {currentStepData.tip}
            </p>
          </div>

          {/* Progress Dots */}
          <div className="flex justify-center space-x-2 mb-6">
            {steps.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentStep(index)}
                className={`w-2.5 h-2.5 rounded-full transition-all ${
                  index === currentStep 
                    ? `${colors.button} w-6` 
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to step ${index + 1}`}
              />
            ))}
          </div>

          {/* Navigation */}
          <div className="flex justify-between items-center">
            <button
              onClick={handlePrev}
              disabled={isFirstStep}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                isFirstStep 
                  ? 'text-gray-300 cursor-not-allowed' 
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <ChevronLeft size={20} className="mr-1" />
              Back
            </button>
            
            <span className="text-sm text-gray-500">
              {currentStep + 1} of {steps.length}
            </span>
            
            <button
              onClick={handleNext}
              className={`flex items-center px-6 py-2 ${colors.button} text-white rounded-lg transition-colors`}
            >
              {isLastStep ? "Let's Go!" : 'Next'}
              {!isLastStep && <ChevronRight size={20} className="ml-1" />}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingTour;
